import React, { useCallback, useMemo, useState } from 'react';
import { Switch, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import DataTable from '../../../../components/DataTable';
import { ListPage, buildCogActionsColumn } from '../../../../components/list';
import ContactCategoryViewDrawer from './ContactCategoryViewDrawer';
import ContactCategoryFormDrawer from './ContactCategoryFormDrawer';
import { updateContactCategory } from '../api/contactCategories';
import { useContactCategories } from '../hooks/useContactCategories';
import { useContactCategory } from '../hooks/useContactCategory';
import { useCreateContactCategory } from '../hooks/useCreateContactCategory';
import { useUpdateContactCategory } from '../hooks/useUpdateContactCategory';
import { useDeleteContactCategory } from '../hooks/useDeleteContactCategory';
import type { ContactCategoryListItem, ContactCategoryFormValues } from '../types';

const ContactCategoriesPage: React.FC = () => {
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const [formDrawerOpen, setFormDrawerOpen] = useState(false);
  const [formMode, setFormMode] = useState<'add' | 'edit'>('add');
  const [formCategoryId, setFormCategoryId] = useState<number | null>(null);

  const { data: categories = [], isLoading: loadingList } = useContactCategories();
  const { data: categoryDetail, isLoading: loadingCategory } = useContactCategory(
    formMode === 'edit' ? formCategoryId : null,
  );
  const viewCategory = useContactCategory(selectedCategoryId);
  const queryClient = useQueryClient();
  const deleteMutation = useDeleteContactCategory();

  const sortedCategories = useMemo(
    () => [...categories].sort((a, b) => a.sortOrder - b.sortOrder),
    [categories],
  );

  const toggleMutation = useMutation({
    mutationFn: ({ id, values }: { id: number; values: ContactCategoryFormValues }) =>
      updateContactCategory(id, values),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['settings-contact-categories'] });
      void queryClient.invalidateQueries({ queryKey: ['contact-categories'] });
    },
  });

  const createMutation = useCreateContactCategory(() => {
    setFormDrawerOpen(false);
  });
  const updateMutation = useUpdateContactCategory(formCategoryId, () => {
    setFormDrawerOpen(false);
  });

  const handleAdd = useCallback(() => {
    setFormMode('add');
    setFormCategoryId(null);
    setFormDrawerOpen(true);
  }, []);

  const handleEdit = useCallback((id: number) => {
    setFormMode('edit');
    setFormCategoryId(id);
    setFormDrawerOpen(true);
  }, []);

  const columns: ColumnsType<ContactCategoryListItem> = useMemo(
    () => [
      {
        title: 'Label',
        dataIndex: 'label',
        key: 'label',
        render: (v: string) => <Typography.Text strong>{v}</Typography.Text>,
      },
      {
        title: 'Code',
        dataIndex: 'code',
        key: 'code',
        width: 140,
      },
      {
        title: 'Sort',
        dataIndex: 'sortOrder',
        key: 'sortOrder',
        width: 80,
      },
      {
        title: 'Contacts',
        dataIndex: 'contactCount',
        key: 'contactCount',
        width: 100,
        align: 'center',
      },
      {
        title: 'Customer segment',
        dataIndex: 'customerSegmentTitle',
        key: 'customerSegmentTitle',
        ellipsis: true,
      },
      {
        title: 'Active',
        key: 'status',
        width: 100,
        render: (_: unknown, record: ContactCategoryListItem) => (
          <Switch
            checked={record.isActive}
            loading={
              toggleMutation.isPending && toggleMutation.variables?.id === record.id
            }
            onChange={(checked) => {
              toggleMutation.mutate({
                id: record.id,
                values: {
                  label: record.label,
                  sortOrder: record.sortOrder,
                  customerSegmentId: record.customerSegmentId,
                  isActive: checked,
                },
              });
            }}
          />
        ),
      },
      buildCogActionsColumn<ContactCategoryListItem>({
        onView: (record) => setSelectedCategoryId(record.id),
        onEdit: (record) => handleEdit(record.id),
        onDelete: (record) => deleteMutation.mutate(record.id),
        editPermission: 'contact-categories.edit',
        deletePermission: 'contact-categories.delete',
        deleteConfirm: () => ({
          title: 'Delete contact category',
          content: 'Delete this category? Contacts using it must be reassigned first.',
        }),
        rowNumber: (_record, index) => index + 1,
      }),
    ],
    [deleteMutation, toggleMutation, handleEdit],
  );

  const handleEditFromView = () => {
    if (selectedCategoryId !== null) {
      handleEdit(selectedCategoryId);
    }
  };

  const handleSaveForm = (values: ContactCategoryFormValues) => {
    if (formMode === 'add') {
      createMutation.mutate(values);
    } else if (formCategoryId !== null) {
      updateMutation.mutate(values);
    }
  };

  return (
    <>
      <ListPage
        title="Customer Categories"
        subtitle="Classify contacts for filtering, reporting, and lead routing."
        primaryAction={{
          permission: 'contact-categories.create',
          label: 'Add category',
          onClick: handleAdd,
        }}
      >
        <DataTable<ContactCategoryListItem>
          columns={columns}
          dataSource={sortedCategories}
          loading={loadingList}
          rowKey="id"
          bordered
          striped
          pagination={false}
          emptyText="No contact categories yet. Add one to classify contacts."
        />
      </ListPage>

      <ContactCategoryViewDrawer
        open={selectedCategoryId !== null}
        onClose={() => setSelectedCategoryId(null)}
        contactCategory={viewCategory.data}
        loading={viewCategory.isLoading}
        onEdit={handleEditFromView}
      />

      <ContactCategoryFormDrawer
        open={formDrawerOpen}
        onClose={() => setFormDrawerOpen(false)}
        mode={formMode}
        contactCategory={categoryDetail}
        loadingContactCategory={formMode === 'edit' && formCategoryId !== null ? loadingCategory : false}
        onSave={handleSaveForm}
        saving={createMutation.isPending || updateMutation.isPending}
      />
    </>
  );
};

export default ContactCategoriesPage;
